import React from 'react';
import cx from 'classnames';
import {useTranslation} from 'react-i18next';

import {
    Avatar,
    AvatarType,
    Button,
    Text,
    FontVariant,
    Tooltip,
} from '@pexip/components';

import {TestId, type InMeetingParticipant} from '../..';

import styles from './ChatNewDirectMessage.module.scss';

const ParticipantRow: React.FC<{
    displayName?: string;
}> = ({displayName}) => (
    <div className={styles.row}>
        <Avatar
            colorScheme="dark"
            displayName={displayName}
            sizeModifier={'small'}
            type={AvatarType.Initials}
            className="mr-2"
        />
        <Text fontVariant={FontVariant.BodyBold} isTruncated>
            {displayName}
        </Text>
    </div>
);

export const ChatNewDirectMessage: React.FC<
    React.ComponentProps<'div'> & {
        participant: InMeetingParticipant;
        onMessageClick: (participant: InMeetingParticipant) => void;
    }
> = ({className, participant, onMessageClick}) => {
    const {t} = useTranslation();
    return (
        <div className={cx(styles.container, className)}>
            <ParticipantRow displayName={participant.displayName} />
            <Button
                data-testid={TestId.ButtonChatNewDirectMessage}
                onClick={() => onMessageClick(participant)}
                size="compact"
                variant="tertiary"
            >
                {t('meeting.chat-message-participant', 'Message')}
            </Button>
        </div>
    );
};

export const ChatUnsupportedDirectMessage: React.FC<
    React.ComponentProps<'div'> & {
        participant: InMeetingParticipant;
    }
> = ({className, participant}) => {
    const {t} = useTranslation();
    return (
        <div className={cx(styles.container, className)}>
            <ParticipantRow displayName={participant.displayName} />
            <Tooltip
                text={t(
                    'meeting.chat-message-participant-no-direct-chat',
                    "You can't send a message to this participant because their device doesn't support direct chat.",
                )}
                className={styles.tooltip}
            >
                <Button
                    data-testid={TestId.ButtonChatNewDirectMessage}
                    isDisabled
                    size="compact"
                    variant="tertiary"
                >
                    {t('meeting.chat-message-participant', 'Message')}
                </Button>
            </Tooltip>
        </div>
    );
};
